import '@rainbow-me/rainbowkit/styles.css';
import { ConnectButton, RainbowKitProvider, getDefaultWallets } from '@rainbow-me/rainbowkit';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import QRCode from 'react-qr-code';
import { WagmiConfig, configureChains, createConfig, mainnet, useAccount, useSignMessage } from 'wagmi';
import styles from '../styles/Login.module.css';
import Image from "next/legacy/image";
import wordmark from '../public/images/wordmark.png';
import salsaIcon from '../public/images/salsa_icon.png';
import sign from '../public/images/sign.svg';
import downloadQr from '../public/images/download_qr.png';
import warning from '../public/images/warning.svg';
import { APP_STORE_LINK, PLAY_STORE_LINK } from './download';
import { infuraProvider } from 'wagmi/providers/infura';
import { publicProvider } from 'wagmi/providers/public';

const INFURA_ID = "a71874bbcb6a450398f24a7bbd436eda";

const { chains, publicClient } = configureChains(
  [mainnet],
  [
    infuraProvider({ apiKey: INFURA_ID }),
    publicProvider()
  ]
);

const { connectors } = getDefaultWallets({
  appName: 'Salsa: Web3 Inbox',
  projectId: 'c7aa1f9f17b9493f7a1f489b6f9813df', // wallet connect project id
  chains
});

const config = createConfig({
  autoConnect: true,
  publicClient,
  connectors,
});

const WrappedLogin = () => {
    return (
        <WagmiConfig config={config}>
            <RainbowKitProvider chains={chains}>
                <Login/>
            </RainbowKitProvider>
        </WagmiConfig>
    );
}

const Login = () => {
    const router = useRouter();
    const [mounted, setMounted] = useState(false)

    const { address, isConnected } = useAccount({ onDisconnect: logout });

    const [messageToSign, setMessageToSign] = useState<string>();
    const [verifying, setVerifying] = useState(false);
    const [qrLoginToken, setQrLoginToken] = useState<string>();
    const [tokenExpired, setTokenExpired] = useState(false);

    const { signMessage } = useSignMessage({
        async onSuccess(signature) {
            try {
                await fetch('/api/verifySignatureV2', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    // TODO: fix the ens part
                    body: JSON.stringify({ address, signature, ens: address, desktop: true }),
                });
                router.reload();
            } catch (e) {
                // TODO: send to Sentry
            } finally {
                setVerifying(false);
            }
        },
    })

    async function logout() {
        await fetch('/api/auth/logout');
        setQrLoginToken(undefined);
    }

    function finishLogin() {
        if (!messageToSign) {
            return;
        }
        signMessage({ message: messageToSign });
    }

    useEffect(() => setMounted(true), []);

    useEffect(() => {
        async function getNonce() {
            if (!isConnected) {
                return;
            }
            const { message, id } = await fetch('/api/getNonce', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ address }),
            }).then(res => res.json());
            if (id) {
                setQrLoginToken(id);
                setTimeout(() => setTokenExpired(true), 15 * 60 * 1000);    // 15 minutes
            } else {
                setMessageToSign(message);
            }
        }

        getNonce();
    }, [isConnected, address])

    function getDownloadLink() {
        const userAgent = navigator.userAgent || navigator.vendor;

        if (/android/i.test(userAgent)) {
            return PLAY_STORE_LINK;
        }

        if (/iPad|iPhone|iPod/.test(userAgent)) {
            return APP_STORE_LINK;
        }

        return '/download';
    }


    if (!mounted) {
        // https://github.com/wagmi-dev/wagmi/issues/542#issuecomment-1144178142
        return null;
    }

    const PageContainer = ({ children }: { children: React.ReactNode }) => {
        return <div className={styles.page}>
            <div className={styles.header}>
                <div className={styles.wordmarkImage}>
                    <Image src={wordmark} alt="Salsa wordmark" />
                </div>
                {isConnected && <ConnectButton showBalance={false} />}
            </div>

            <div className={styles.container}>
                {children}
            </div>
        </div>
    }


    if (!isConnected) {
        return (
            <PageContainer>
                <div className={styles.loginImage}>
                    <Image src={salsaIcon} alt="Salsa icon" />
                </div>

                <div className={styles.connectContainer}>
                    <div className={styles.title}>Welcome to Salsa!</div>
                    <div className={styles.bodyText}>Login to your Salsa mobile application using your browser.</div>
                    <div className={styles.connectButton}>
                        <ConnectButton showBalance={false} />
                    </div>

                    <div className={styles.separator} />

                    <div className={styles.downloadContainer}>
                        <Image className={styles.qrCode} src={downloadQr} width={86.5} height={86.5} alt="Download QR code" />
                        <div className={styles.downloadTexts}>
                            <div className={styles.subTitle}>Don&apos;t have the app yet?</div>
                            <div className={styles.text}><a href={getDownloadLink()}><u>Download</u></a> the Salsa App on iOS or Android</div>
                        </div>
                    </div>
                </div>
            </PageContainer>
        )
    };

    if (!qrLoginToken) {
        return (
            <PageContainer>
                <div className={styles.loginImage}>
                    <Image src={sign} alt="Sign" />
                </div>

                <div className={styles.connectContainer}>
                    <div className={styles.title}>Sign Transaction</div>
                    <div className={styles.bodyText}>To finish authenticating, click the button below and then sign the transaction</div>

                    <div className={styles.loginContainer}>
                        <a className={`${styles.button} ${verifying ? styles.disabledButton : ''}`} onClick={finishLogin}>
                            {verifying ? "Finishing Login..." : "Finish Login"}
                        </a>
                    </div>
                </div>
            </PageContainer>
        );
    }

    return (
        <PageContainer>
            {tokenExpired ?
                <div className={styles.errorText}>This code has expired. Please refresh to obtain a new one.</div>
                :
                <div className={styles.qrWrapper}>
                    <QRCode value={qrLoginToken} className={styles.loginQrCode} />
                </div>}

            <div className={styles.connectContainer}>
                <div className={styles.title}>You&apos;re In</div>
                <div className={styles.bodyText}>Open the Salsa app, tap “Connect” & select “Login with Desktop” from the list of options and scan this QR code.</div>

                <div className={styles.warnContainer}>
                    <Image src={warning} width={25} height={24} alt="⚠" />

                    <div className={styles.warnText}>Do not screenshot or share this QR code or someone can login to your Salsa account.</div>
                </div>
            </div>
        </PageContainer>
    );
}


export default WrappedLogin;